import { useMemo, useCallback } from 'react';
import { GetSitesQuery, SitesFilterInput, useGetSitesPaginatedQuery } from 'data/__generated__';
import { Option, TotemSelect, TotemSelectProps } from 'components/TotemSelect';

export type SiteSelected = GetSitesQuery['sites'][number];

export function SiteSelect({
    value,
    onChange,
    dataTest,
    width,
    filters,
    ...props
}: Omit<Extract<TotemSelectProps<string>, { isMulti?: false }>, 'value' | 'onChange'> & {
    value: string | null;
    onChange: (site: string | null) => void;
    width?: string;
    filters?: SitesFilterInput;
}) {
    const { data: sitesData, refetch } = useGetSitesPaginatedQuery({
        variables: {
            offset: 0,
            filters: {
                ...(filters ?? {}),
                searchValue: value || '',
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const defaultOptions = useMemo(
        () =>
            (sitesData?.sitesFilter.sites || []).map((site) => ({
                label: site.name,
                value: site._id,
            })),
        [sitesData],
    );

    const fetchOptions = useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const result = await refetch({
                offset: 0,
                filters: {
                    ...(filters ?? {}),
                    searchValue: query || '',
                },
            });

            return (result.data?.sitesFilter.sites || []).map((site) => ({
                label: site.name,
                value: site._id,
            }));
        },
        [refetch, filters],
    );

    return (
        <div className="min-w-[450px]" style={{ width: width ?? '450px' }}>
            <TotemSelect<string>
                isClearable
                dataTest={dataTest}
                label="Choisir un site"
                placeholder="Sélectionner un site (recherche par nom ou par _id)"
                value={value}
                onChange={onChange}
                fetchOptions={fetchOptions}
                defaultOptions={defaultOptions}
                {...props}
            />
        </div>
    );
}
